var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { ProductSelection } from "./ProductSelection.chunk.mjs";
import { jsx } from "react/jsx-runtime";
import { ContentBlock, Loader } from "braid-design-system";
import { useLanguage } from "@vocab/react";
import { useGetAdProductsForAdPostingQuery } from "./styles/ProductSelection/components/layouts/GridLayout/GridLayout.mjs";
import { GetAdProductsForAdEditingDocument, GetAdProductsForAdPostingDocument, PurchaseContextUseCase } from "./styles/ProductSelection/components/layouts/GridLayout/GridLayout.mjs";
import { useApolloClient } from "@apollo/client";
import { ShoppingCartContext } from "@seek/shopping-cart-provider";
import { useContext, useCallback, useEffect } from "react";
class GetProductSetError extends Error {
  constructor(message) {
    super(`Failed to get product set with error: ${message}`);
    __publicField(this, "errorCode", "PRODUCT_SET_ERROR");
    this.name = "ProductSetError";
  }
}
const useProductSet = ({
  advertiserId,
  productSetId,
  draftId
}) => {
  const { language } = useLanguage();
  const skip = isNaN(Number(advertiserId)) || !productSetId;
  const {
    loading,
    error,
    data: productSet
  } = useGetAdProductsForAdPostingQuery({
    variables: {
      input: { advertiserId, productSetId, locale: language },
      draftId
    },
    skip
  });
  if (skip) {
    return { loading: true, error, adProducts: void 0, display: void 0 };
  }
  if (productSet) {
    if (productSet.adProductsForAdPosting.__typename === "ProductSetExpiredError" || productSet.adProductsForAdPosting.__typename === "ProductSetNotFoundError") {
      const retryableError = new GetProductSetError(
        productSet.adProductsForAdPosting.message
      );
      return {
        loading,
        error: retryableError,
        adProducts: void 0,
        display: void 0
      };
    }
    if (productSet.adProductsForAdPosting.__typename !== "ProductList") {
      const productSetError = new Error(
        `adProductsForAdPosting must be a ProductList. Received ${productSet == null ? void 0 : productSet.adProductsForAdPosting.__typename} instead.`
      );
      return {
        loading,
        error: productSetError,
        adProducts: void 0,
        display: void 0
      };
    }
    const display = productSet.adProductsForAdPosting.display;
    const adProducts = productSet.adProductsForAdPosting.products.filter(
      (product) => product && product.__typename === "AdProduct"
    );
    return { display, adProducts, loading, error };
  }
  return { loading, error, adProducts: void 0, display: void 0 };
};
const ProductSelectionConnected = ({
  errorComponent,
  onError,
  onProductSelect,
  selectedProductIds,
  context,
  onTrackingEvent,
  layout
}) => {
  var _a;
  const { loading, error, adProducts, display } = useProductSet({
    advertiserId: context.hirer.id,
    productSetId: context.products.productSetId,
    draftId: (_a = context.draft) == null ? void 0 : _a.id
  });
  if (loading && !adProducts) {
    return /* @__PURE__ */ jsx(ContentBlock, { width: "medium", children: /* @__PURE__ */ jsx(Loader, { delayVisibility: true }) });
  }
  if (error || !adProducts || adProducts.length === 0) {
    onError(error ?? new Error("Did not receive any ad products"));
    return errorComponent;
  }
  return /* @__PURE__ */ jsx(
    ProductSelection,
    {
      layoutDisplay: display,
      products: adProducts,
      selectedProductIds,
      onProductSelect,
      context,
      onTrackingEvent,
      layout
    }
  );
};
const ProductSelectionConnectedCart = ({
  onProductSelect,
  context,
  onTrackingEvent,
  layout,
  shoppingCart
}) => {
  var _a;
  const shoppingCartContext = useContext(ShoppingCartContext);
  const {
    getAdProductsDocument,
    subscriptionResponseKey,
    key: subscriptionUniqueIdKey,
    getAdProductsDocumentConstructVariables
  } = shoppingCart;
  const {
    productResponses,
    cartState,
    cartProductInProgress,
    addLineItem,
    removeLineItem,
    subscribeForProducts,
    unsubscribeForProducts
  } = shoppingCartContext;
  const client = useApolloClient();
  const getAdProductsQuery = useCallback(
    async (productSetId) => await client.query({
      query: getAdProductsDocument,
      variables: getAdProductsDocumentConstructVariables(productSetId),
      fetchPolicy: "no-cache"
    }),
    [getAdProductsDocument, getAdProductsDocumentConstructVariables, client]
  );
  useEffect(() => {
    subscribeForProducts(
      subscriptionUniqueIdKey,
      subscriptionResponseKey,
      getAdProductsQuery
    );
    return () => {
      if (typeof unsubscribeForProducts !== "undefined")
        unsubscribeForProducts(subscriptionUniqueIdKey);
    };
  }, [
    getAdProductsQuery,
    subscribeForProducts,
    subscriptionUniqueIdKey,
    subscriptionResponseKey,
    unsubscribeForProducts
  ]);
  const productResponse = productResponses[subscriptionUniqueIdKey];
  const onProductSelectHandler = (productId, productType, intent) => {
    switch (intent) {
      case "add":
      case "update":
        addLineItem({ productId, productType });
        break;
      case "remove":
        removeLineItem({ productId, productType });
        break;
    }
    if (onProductSelect) {
      onProductSelect(productId);
    }
  };
  const selectedProductIds = ((cartState == null ? void 0 : cartState.items) ?? []).map(
    (cartItem) => cartItem.product.id
  );
  const loadingProductIds = Object.keys(cartProductInProgress);
  const products = (_a = productResponse == null ? void 0 : productResponse.products) == null ? void 0 : _a.products;
  const layoutDisplay = productResponse == null ? void 0 : productResponse.display;
  const waitingForProductSubscription = productResponse === void 0 && products === void 0;
  if (waitingForProductSubscription || (productResponse == null ? void 0 : productResponse.loading) && !(products == null ? void 0 : products.length)) {
    return /* @__PURE__ */ jsx(ContentBlock, { width: "medium", children: /* @__PURE__ */ jsx(Loader, { delayVisibility: true }) });
  }
  if (productResponse == null ? void 0 : productResponse.error) {
    throw productResponse == null ? void 0 : productResponse.error;
  }
  if (products && !products.length) {
    throw new Error("PRODUCT_SELECTION_NO_PRODUCTS_ERROR");
  }
  return /* @__PURE__ */ jsx(
    ProductSelection,
    {
      products: products ?? [],
      layoutDisplay,
      selectedProductIds,
      loadingProductIds,
      onProductSelect: onProductSelectHandler,
      context,
      onTrackingEvent,
      layout
    }
  );
};
export {
  ProductSelection as AdProductSelection,
  ProductSelectionConnected as AdProductSelectionConnected,
  ProductSelectionConnectedCart as AdProductSelectionConnectedCart,
  GetAdProductsForAdEditingDocument,
  GetAdProductsForAdPostingDocument,
  PurchaseContextUseCase
};
